/**
 * TableRow - Individual row view for TableView
 *
 * Extends ListViewItem to render table rows with proper cell formatting
 * and support for all table features like selection, actions, and context menus.
 *
 * @example
 * const row = new TableRow({
 *   model: userModel,
 *   columns: tableColumns,
 *   actions: ['view', 'edit', 'delete']
 * });
 */

import ListViewItem from '../list/ListViewItem.js';
import dataFormatter from '@core/utils/DataFormatter.js';

class TableRow extends ListViewItem {
  constructor(options = {}) {
    super({
      tagName: 'tr',
      className: 'table-row',
      enableTooltips: true,
      ...options
    });

    // Table-specific properties
    this.columns = options.columns || [];
    this.actions = options.actions || null;
    this.contextMenu = options.contextMenu || null;
    this.batchActions = options.batchActions || null;
    this.tableView = options.tableView || options.listView || null;

    // Inline editing state
    this.editingCells = new Set(); // Track which cells are being edited

    // Override template to generate table cells
    this.template = this.buildRowTemplate();
  }

  /**
   * Get responsive CSS classes for column visibility
   * @param {string|object} visibility - Bootstrap breakpoint or config object
   *   - String: 'md' = show at md and up (hide below)
   *   - Object: { hide: 'md' } = hide at md and up (show below)
   *   - Object: { show: 'md', hide: 'lg' } = show from md to lg only
   * @returns {string} Bootstrap responsive display classes
   */
  getResponsiveClasses(visibility) {
    if (!visibility) return ''; // Always visible if no visibility specified

    const validBreakpoints = ['sm', 'md', 'lg', 'xl', 'xxl'];

    // Legacy string format: show at breakpoint and up
    if (typeof visibility === 'string') {
      if (!validBreakpoints.includes(visibility)) {
        console.warn(`Invalid visibility breakpoint: ${visibility}. Valid options are: ${validBreakpoints.join(', ')}`);
        return '';
      }
      return `d-none d-${visibility}-table-cell`;
    }

    // Object format for more control
    if (typeof visibility === 'object') {
      const classes = [];

      // Hide at breakpoint and up
      if (visibility.hide) {
        if (!validBreakpoints.includes(visibility.hide)) {
          console.warn(`Invalid hide breakpoint: ${visibility.hide}. Valid options are: ${validBreakpoints.join(', ')}`);
          return '';
        }
        classes.push(`d-table-cell d-${visibility.hide}-none`);
      }

      // Show at breakpoint and up (optionally combined with hide)
      if (visibility.show) {
        if (!validBreakpoints.includes(visibility.show)) {
          console.warn(`Invalid show breakpoint: ${visibility.show}. Valid options are: ${validBreakpoints.join(', ')}`);
          return '';
        }
        if (!visibility.hide) {
          classes.push(`d-none d-${visibility.show}-table-cell`);
        } else {
          classes.push(`d-${visibility.show}-table-cell`);
        }
      }

      return classes.join(' ');
    }

    return '';
  }

  /**
   * Build the row template with table cells
   */
  buildRowTemplate() {
    let template = '';

    // Selection checkbox cell
    if (this.tableView && this.tableView.isSelectable()) {
      template += `
        <td style="padding: 0;">
          <div class="mojo-select-cell {{#selected}}selected{{/selected}}"
               data-action="select">
            <div class="mojo-checkbox">
              <i class="bi bi-check"></i>
            </div>
          </div>
        </td>
      `;
    }

    // Data cells for each column
    this.columns.forEach((column, columnIndex) => {
      const cellClass = column.class || column.className || '';
      const responsiveClasses = this.getResponsiveClasses(column.visibility);
      const editableClass = column.editable ? 'editable-cell' : '';
      const alignClass = this.tableView && this.tableView.getAlignClass
        ? this.tableView.getAlignClass(column.align)
        : '';
      const combinedClasses = [cellClass, responsiveClasses, editableClass, alignClass].filter(c => c).join(' ');
      const cellContent = this.buildCellTemplate(column, columnIndex);

      // Determine cell action
      let cellAction = column.action;
      if (!cellAction && column.editable) {
        cellAction = 'edit-cell';
      } else if (!cellAction && this.tableView.rowAction) {
        cellAction = this.tableView.rowAction;
      }

      if (cellAction) {
        template += `<td class="${combinedClasses}" data-action="${cellAction}" data-column="${column.key}">${cellContent}</td>`;
      } else {
        template += `<td class="${combinedClasses}" data-column="${column.key}">${cellContent}</td>`;
      }
    });

    // Actions cell
    if (this.actions) {
      template += this.buildActionsTemplate();
    } else if (this.contextMenu) {
      template += this.buildContextMenuTemplate();
    }

    return template;
  }

  /**
   * Build template for a single cell
   */
   /**
    * Build template for a single cell
    */
   buildCellTemplate(column, columnIndex = 0) {
       // Build path for Mustache to access the value
       const path = `model.${column.key}`;
       // Support both 'formatter' and 'format' for consistency with DataView
       const formatter = column.formatter || column.format;
       // editable cells need a `.cell-content` wrapper because enterEditMode()
       // hides it and inserts the editor in its place.
       const editableAttr = column.editable ? ` class="cell-content" data-field="${column.key}"` : '';
       if (formatter) {
         // For string formatters that are pipe expressions
         if (typeof formatter === 'string') {
           if (column.editable) {
             return `<span${editableAttr}>{{{${path}|${formatter}}}}</span>`;
           }
           return `{{{${path}|${formatter}}}}`;
         } else if (typeof formatter === 'function') {
           // Keep legacy data-formatter key selector for compatibility, but
           // use a per-column id so duplicate keys can be formatted correctly.
           const cls = column.editable ? 'cell-content' : '';
           const fieldAttr = column.editable ? ` data-field="${column.key}"` : '';
           return `<span class="${cls}" data-formatter="${column.key}" data-formatter-id="${columnIndex}"${fieldAttr}>{{${path}}}</span>`;
         }
       }

       if (column.template) {
         if (column.editable) {
           return `<span${editableAttr}>${column.template}</span>`;
         }
         return column.template;
       }

       // For editable cells, wrap content in a span for easy replacement
       if (column.editable) {
         return `<span${editableAttr}>{{{${path}}}}</span>`;
       }

       return `{{{${path}}}}`;
   }

  /**
   * Build actions cell template
   */
  buildActionsTemplate() {
    if (!this.actions || this.actions.length === 0) return '';

    const buttons = this.actions.map(action => {
      if (typeof action === 'string') {
        switch (action) {
          case 'view':
            return `
              <button class="btn btn-sm btn-outline-primary"
                      data-action="view"
                      title="View">
                <i class="bi bi-eye"></i>
              </button>
            `;

          case 'edit':
            return `
              <button class="btn btn-sm btn-outline-secondary"
                      data-action="edit"
                      title="Edit">
                <i class="bi bi-pencil"></i>
              </button>
            `;

          case 'delete':
            return `
              <button class="btn btn-sm btn-outline-danger"
                      data-action="delete"
                      title="Delete">
                <i class="bi bi-trash"></i>
              </button>
            `;

          default:
            return '';
        }
      } else if (typeof action === 'object') {
        return `
          <button class="btn btn-sm ${action.class || 'btn-outline-primary'}"
                  data-id="${this.model.id}"
                  data-action="${action.action}"
                  title="${action.label || ''}">
            ${action.icon ? `<i class="${action.icon}"></i>` : ''}
            ${action.label && !action.icon ? action.label : ''}
          </button>
        `;
      }
      return '';
    }).join('');

    return `<td><div class="btn-group btn-group-sm">${buttons}</div></td>`;
  }

  /**
   * Build context menu cell template
   */
  buildContextMenuTemplate() {
    if (!this.contextMenu || this.contextMenu.length === 0) return '';

    const items = this.buildContextMenuItems();
    if (!items) return ''; // every actionable item filtered out — no toggle

    return `
      <td class="text-end" style="width: 1px;">
        <div class="dropdown">
          <button class="btn btn-sm btn-link border-0"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style="color: #6c757d;">
            <i class="bi bi-three-dots-vertical"></i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end shadow-sm">
            ${items}
          </ul>
        </div>
      </td>
    `;
  }

  /**
   * Should a context-menu item render for this row? Items can carry
   * `permissions` (any-of, fail-closed via View#checkPermissions) and/or a
   * per-row `visible(model)` predicate. A throwing predicate hides the item
   * rather than breaking the row.
   */
  isMenuItemVisible(menuItem) {
    if (menuItem.permissions && !this.checkPermissions(menuItem.permissions)) {
      return false;
    }
    if (typeof menuItem.visible === 'function') {
      try {
        if (!menuItem.visible(this.model)) return false;
      } catch (error) {
        console.warn(`TableRow contextMenu visible() threw for "${menuItem.label || menuItem.action}"; hiding item:`, error);
        return false;
      }
    }
    return true;
  }

  /**
   * Build context menu items. `action` may be a framework action string
   * (dispatched as `data-action` exactly as before) or a callback invoked
   * with `(model, app)` — callbacks dispatch through `row-context-menu-item`
   * with the item's original index, mirroring ListView's custom toolbar
   * buttons (`data-button-index`).
   */
  buildContextMenuItems() {
    const visibleItems = this.contextMenu
      .map((menuItem, index) => ({ menuItem, index }))
      .filter(({ menuItem }) => this.isMenuItemVisible(menuItem));

    // Dividers alone don't justify a menu.
    if (!visibleItems.some(({ menuItem }) => !(menuItem.separator || menuItem.divider))) {
      return '';
    }

    return visibleItems.map(({ menuItem, index }) => {
      if (menuItem.separator||menuItem.divider) {
        return '<li><hr class="dropdown-divider"></li>';
      }

      let itemClass = 'dropdown-item';
      if (menuItem.action === 'delete' || menuItem.danger) {
        itemClass += ' text-danger';
      }
      if (menuItem.disabled) {
        itemClass += ' disabled';
      }

      // Escape dev-supplied fields that flow into HTML/attributes — same
      // defense-in-depth as ListView's toolbar buttons.
      const actionAttrs = typeof menuItem.action === 'function'
        ? `data-action="row-context-menu-item" data-menu-index="${index}"`
        : `data-action="${this.escapeHtml(menuItem.action)}"`;

      return `
        <li>
          <a class="${itemClass}" href="#"
             data-id="{{model.id}}"
             ${actionAttrs}
             ${menuItem.disabled ? 'aria-disabled="true" tabindex="-1"' : ''}>
            ${menuItem.icon ? `<i class="${this.escapeHtml(menuItem.icon)} me-2"></i>` : ''}
            ${this.escapeHtml(menuItem.label)}
          </a>
        </li>
      `;
    }).join('');
  }

  /**
   * Dispatch a callback context-menu item: look the item up by its original
   * index and invoke `action(model, app)`.
   */
  async onActionRowContextMenuItem(event, element) {
    event.stopPropagation();

    const index = parseInt(element.getAttribute('data-menu-index'), 10);
    const menuItem = this.contextMenu && this.contextMenu[index];
    if (!menuItem || typeof menuItem.action !== 'function') return;

    await menuItem.action(this.model, this.getApp());
  }

  /**
   * Override onAfterRender to apply function formatters and templates
   */
  async onAfterRender() {
    await super.onAfterRender();

    // Apply function formatters
    this.columns.forEach((column, columnIndex) => {
      if (column.formatter && typeof column.formatter === 'function') {
        let cell = this.element.querySelector(`[data-formatter-id="${columnIndex}"]`);
        if (!cell) {
          // Backward-compatible fallback for existing markup/selectors.
          cell = this.element.querySelector(`[data-formatter="${column.key}"]`);
        }
        if (cell) {
          const value = this.model.get ? this.model.get(column.key) : this.model[column.key];
          const context = {
            value,
            row: this.model,  // deprecate this
            model: this.model,
            column,
            table: this.tableView,
            index: this.index
          };
          try {
            cell.innerHTML = column.formatter(value, context);
          } catch (error) {
            console.error(`Error formatting cell for column ${column.key}:`, error);
          }
        }
      }

      // Apply function templates
      // if (column.template && typeof column.template === 'function') {
      //   const cell = this.element.querySelector(`[data-template="${column.key}"]`);
      //   if (cell) {
      //     const value = this.model.get ? this.model.get(column.key) : this.model[column.key];
      //     cell.innerHTML = column.template(value, this.model);
      //   }
      // }
    });

    // Update selection state
    if (this.selected) {
      this.element.classList.add('selected');
    }

    // Set data-id attribute for easy identification
    const id = this.model.get ? this.model.get('id') : this.model.id;
    if (id) {
      this.element.setAttribute('data-id', id);
    }
  }

  /**
   * Handle edit cell action
   */
  async onActionEditCell(event, element) {
    event.stopPropagation();

    const columnKey = element.getAttribute('data-column');
    const column = this.columns.find(col => col.key === columnKey);

    if (!column || !column.editable) return;

    // Don't enter edit mode if already editing this cell
    if (this.editingCells.has(columnKey)) return;

    await this.enterEditMode(columnKey, column, element);
  }

  /**
   * Handle row click action
   */
  async onActionRowClick(event, element) {
    // Don't trigger row click if clicking on action buttons or editing
    if (event.target.closest('.btn-group') || event.target.closest('.dropdown') || event.target.closest('.cell-editor')) {
      return;
    }

    // Emit row click event
    this.emit('row:click', {
      row: this,
      model: this.model,
      column: element.getAttribute('data-column'),
      event: event
    });

    // Notify parent TableView
    if (this.tableView) {
      this.tableView.emit('row:click', {
        row: this,
        model: this.model,
        column: element.getAttribute('data-column'),
        event: event
      });
    }
  }

  /**
   * Handle view action
   */
  async onActionView(event, element) {
    event.stopPropagation();

    this.emit('row:view', {
      row: this,
      model: this.model,
      event: event
    });

    if (this.tableView) {
      this.tableView.emit('row:view', {
        row: this,
        model: this.model,
        event: event
      });
    }
  }

  /**
   * Handle edit action
   */
  async onActionEdit(event, element) {
    event.stopPropagation();

    this.emit('row:edit', {
      row: this,
      model: this.model,
      event: event
    });

    if (this.tableView) {
      this.tableView.emit('row:edit', {
        row: this,
        model: this.model,
        event: event
      });
    }
      return true;
  }

  /**
   * Handle delete action
   */
  async onActionDelete(event, element) {
    event.stopPropagation();

    this.emit('row:delete', {
      row: this,
      model: this.model,
      event: event
    });

    if (this.tableView) {
      this.tableView.emit('row:delete', {
        row: this,
        model: this.model,
        event: event
      });
    }
  }

  /**
   * Enter edit mode for a cell
   */
  async enterEditMode(columnKey, column, cellElement) {
    const contentSpan = cellElement.querySelector('.cell-content');
    if (!contentSpan) return;

    this.editingCells.add(columnKey);
    const currentValue = this.model.get ? this.model.get(columnKey) : this.model[columnKey];

    // Create editor based on column configuration
    const editor = this.createCellEditor(column, currentValue);

    // Replace content with editor
    const originalContent = contentSpan.innerHTML;
    contentSpan.style.display = 'none';

    const editorContainer = document.createElement('div');
    editorContainer.className = 'cell-editor';
    editorContainer.innerHTML = editor;
    cellElement.appendChild(editorContainer);

    // Focus the input
    const input = editorContainer.querySelector('input, select, .form-check-input');
    if (input) {
      input.focus();
      if (input.type === 'text' || input.type === 'textarea') {
        input.select();
      }
    }

    // Store original content for cancel
    editorContainer.dataset.originalContent = originalContent;
    editorContainer.dataset.columnKey = columnKey;

    // Set up event listeners
    this.setupEditorEvents(editorContainer, columnKey, column);

    this.emit('cell:edit', {
      row: this,
      model: this.model,
      column: columnKey,
      originalValue: currentValue
    });
  }

  /**
   * Create cell editor HTML based on column configuration
   */
  createCellEditor(column, currentValue) {
    const options = column.editableOptions || {};

    switch (options.type) {
      case 'select':
        return this.createSelectEditor(options, currentValue);
      case 'switch':
      case 'checkbox':
        return this.createSwitchEditor(options, currentValue);
      case 'textarea':
        return this.createTextareaEditor(options, currentValue);
      default:
        return this.createTextEditor(options, currentValue);
    }
  }

  /**
   * Create text input editor
   */
  createTextEditor(options, currentValue) {
    const placeholder = options.placeholder || '';
    const inputType = options.inputType || 'text';

    return `
      <div class="d-flex gap-1 align-items-center">
        <input type="${inputType}"
               class="form-control form-control-sm cell-input"
               value="${this.escapeHtml(currentValue || '')}"
               placeholder="${placeholder}">
        <button type="button" class="btn btn-sm btn-success cell-save" title="Save">
          <i class="bi bi-check"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary cell-cancel" title="Cancel">
          <i class="bi bi-x"></i>
        </button>
      </div>
    `;
  }

  /**
   * Create textarea editor
   */
  createTextareaEditor(options, currentValue) {
    const placeholder = options.placeholder || '';
    const rows = options.rows || 2;

    return `
      <div class="d-flex gap-1">
        <textarea class="form-control form-control-sm cell-input"
                  rows="${rows}"
                  placeholder="${placeholder}">${this.escapeHtml(currentValue || '')}</textarea>
        <div class="d-flex flex-column gap-1">
          <button type="button" class="btn btn-sm btn-success cell-save" title="Save">
            <i class="bi bi-check"></i>
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary cell-cancel" title="Cancel">
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Create select dropdown editor
   */
  createSelectEditor(options, currentValue) {
    const optionsArray = options.options || [];
    let optionsHtml = '';

    optionsArray.forEach(option => {
      if (typeof option === 'string') {
        const selected = option === currentValue ? 'selected' : '';
        optionsHtml += `<option value="${option}" ${selected}>${option}</option>`;
      } else if (typeof option === 'object' && option.value !== undefined) {
        const selected = option.value === currentValue ? 'selected' : '';
        optionsHtml += `<option value="${option.value}" ${selected}>${option.label || option.value}</option>`;
      }
    });

    return `
      <div class="d-flex gap-1 align-items-center">
        <select class="form-select form-select-sm cell-input">
          ${optionsHtml}
        </select>
        <button type="button" class="btn btn-sm btn-success cell-save" title="Save">
          <i class="bi bi-check"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary cell-cancel" title="Cancel">
          <i class="bi bi-x"></i>
        </button>
      </div>
    `;
  }

  /**
   * Create switch/checkbox editor
   */
  createSwitchEditor(options, currentValue) {
    const checked = currentValue ? 'checked' : '';
    const switchType = options.type === 'switch' ? 'form-switch' : '';

    return `
      <div class="d-flex gap-2 align-items-center">
        <div class="form-check ${switchType}">
          <input class="form-check-input cell-input" type="checkbox" ${checked}>
        </div>
        <div class="d-flex gap-1">
          <button type="button" class="btn btn-sm btn-success cell-save" title="Save">
            <i class="bi bi-check"></i>
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary cell-cancel" title="Cancel">
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Setup event listeners for cell editor
   */
  setupEditorEvents(editorContainer, columnKey, column) {
    const input = editorContainer.querySelector('.cell-input');
    const saveBtn = editorContainer.querySelector('.cell-save');
    const cancelBtn = editorContainer.querySelector('.cell-cancel');

    // Save on Enter (for text inputs)
    if (input && (input.type === 'text' || input.type === 'email' || input.type === 'number')) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.saveCellEdit(editorContainer, columnKey, column);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.cancelCellEdit(editorContainer, columnKey);
        }
      });
    }

    // Save on change for selects and checkboxes (if auto-save enabled)
    if (input && (input.type === 'checkbox' || input.tagName === 'SELECT') && column.autoSave !== false) {
      input.addEventListener('change', () => {
        this.saveCellEdit(editorContainer, columnKey, column);
      });
    }

    // Button events
    saveBtn?.addEventListener('click', () => {
      this.saveCellEdit(editorContainer, columnKey, column);
    });

    cancelBtn?.addEventListener('click', () => {
      this.cancelCellEdit(editorContainer, columnKey);
    });
  }

  /**
   * Save cell edit
   */
  async saveCellEdit(editorContainer, columnKey, column) {
    const input = editorContainer.querySelector('.cell-input');
    if (!input) return;

    let newValue;

    // Extract value based on input type
    if (input.type === 'checkbox') {
      newValue = input.checked;
    } else if (input.tagName === 'SELECT') {
      newValue = input.value;
    } else {
      newValue = input.value;
    }

    const oldValue = this.model.get ? this.model.get(columnKey) : this.model[columnKey];

    // Save to model and backend
    try {
      if (this.model.save) {
        await this.model.save({ [columnKey]: newValue });
      } else {
        // Fallback for models without save method
        this.model[columnKey] = newValue;
      }

      // Exit edit mode
      this.exitEditMode(editorContainer, columnKey, newValue);

      // Emit save event
      this.emit('cell:save', {
        row: this,
        model: this.model,
        column: columnKey,
        oldValue: oldValue,
        newValue: newValue
      });

    } catch (error) {
      // Show error and keep in edit mode
      console.error('Failed to save cell edit:', error);
      this.emit('cell:save:error', {
        row: this,
        model: this.model,
        column: columnKey,
        oldValue: oldValue,
        newValue: newValue,
        error: error
      });

      // Could show an error message in the UI
      editorContainer.classList.add('saving-error');
      setTimeout(() => editorContainer.classList.remove('saving-error'), 3000);
    }
  }

  /**
   * Cancel cell edit
   */
  cancelCellEdit(editorContainer, columnKey) {
    const originalContent = editorContainer.dataset.originalContent;
    this.exitEditMode(editorContainer, columnKey, null, originalContent);

    this.emit('cell:cancel', {
      row: this,
      model: this.model,
      column: columnKey
    });
  }

  /**
   * Exit edit mode and restore content
   */
  exitEditMode(editorContainer, columnKey, newValue = null, originalContent = null) {
    const cellElement = editorContainer.closest('td');
    const contentSpan = cellElement.querySelector('.cell-content');

    if (contentSpan) {
      if (newValue !== null) {
        // Update display with new value (with proper formatting if needed)
        const column = this.columns.find(col => col.key === columnKey);
        let displayValue = newValue;

        if (column && column.formatter && typeof column.formatter === 'string') {
          displayValue = dataFormatter.pipe(newValue, column.formatter);
        }

        contentSpan.innerHTML = this.escapeHtml(displayValue);
      } else if (originalContent) {
        // Restore original content on cancel
        contentSpan.innerHTML = originalContent;
      }

      contentSpan.style.display = '';
    }

    // Remove editor
    editorContainer.remove();
    this.editingCells.delete(columnKey);
  }

  /**
   * Escape HTML for safe display. Explicit replacement (same as ListView's)
   * rather than div.textContent/innerHTML, because this is also used in
   * attribute contexts (data-action, editor value="...") where quotes must
   * be escaped too.
   */
  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Override select to handle table-specific selection UI
   */
  select() {
    super.select();
    this.addClass('selected');

    // Update checkbox visual state
    const selectCell = this.element?.querySelector('.mojo-select-cell');
    if (selectCell) {
      selectCell.classList.add('selected');
    }
  }

  /**
   * Override deselect to handle table-specific selection UI
   */
  deselect() {
    super.deselect();
    this.removeClass('selected');

    // Update checkbox visual state
    const selectCell = this.element?.querySelector('.mojo-select-cell');
    if (selectCell) {
      selectCell.classList.remove('selected');
    }
  }
}

export default TableRow;
